import { bookingRecordAssetId } from './bookingListDisplay';
import { bookingRecordIsTerminal, bookingRecordPrimaryId } from './bookingRecordMutate';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local calendar day `YYYY-MM-DD` (sortable). */
export function toCalendarDayKey(year: number, monthIndex: number, day: number): string {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

export function parseCalendarDayKey(key: string): { year: number; monthIndex: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.trim());
  if (!m) {
    return null;
  }
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) {
    return null;
  }
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) {
    return null;
  }
  const max = new Date(year, monthIndex + 1, 0).getDate();
  if (day > max) {
    return null;
  }
  return { year, monthIndex, day };
}

export function isoToCalendarDayKey(iso: unknown): string | null {
  if (typeof iso !== 'string' || !iso.trim()) {
    return null;
  }
  const dt = new Date(iso.trim());
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  return toCalendarDayKey(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

export function todayCalendarDayKey(now: Date = new Date()): string {
  return toCalendarDayKey(now.getFullYear(), now.getMonth(), now.getDate());
}

export function isPastCalendarDayKey(key: string, now: Date = new Date()): boolean {
  return key < todayCalendarDayKey(now);
}

/** Inclusive list of day keys from `startKey` through `endKey`. */
export function eachCalendarDayInRange(startKey: string, endKey: string): string[] {
  const a = parseCalendarDayKey(startKey);
  const b = parseCalendarDayKey(endKey);
  if (!a || !b) {
    return [];
  }
  let from = startKey;
  let to = endKey;
  if (from > to) {
    const tmp = from;
    from = to;
    to = tmp;
  }
  const out: string[] = [];
  const cur = parseCalendarDayKey(from)!;
  const end = parseCalendarDayKey(to)!;
  const d = new Date(cur.year, cur.monthIndex, cur.day);
  const endMs = new Date(end.year, end.monthIndex, end.day).getTime();
  while (d.getTime() <= endMs) {
    out.push(toCalendarDayKey(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export function rangeIncludesReservedDay(
  startKey: string,
  endKey: string,
  reserved: ReadonlySet<string>,
): boolean {
  for (const key of eachCalendarDayInRange(startKey, endKey)) {
    if (reserved.has(key)) {
      return true;
    }
  }
  return false;
}

/** Each local calendar day touched by a non-terminal booking on the asset. */
export function collectReservedDayKeysForAsset(
  bookings: Record<string, unknown>[],
  assetId: string,
  options?: { excludeBookingId?: string },
): Set<string> {
  const want = assetId.trim().toLowerCase();
  const excludeId = options?.excludeBookingId?.trim().toLowerCase() ?? '';
  const reserved = new Set<string>();
  if (!want) {
    return reserved;
  }
  for (const booking of bookings) {
    if (bookingRecordIsTerminal(booking)) {
      continue;
    }
    if (excludeId) {
      const rowId = bookingRecordPrimaryId(booking);
      if (rowId && rowId.toLowerCase() === excludeId) {
        continue;
      }
    }
    const bid = bookingRecordAssetId(booking);
    if (!bid || bid.toLowerCase() !== want) {
      continue;
    }
    const startKey = isoToCalendarDayKey(booking.start_date);
    const endKey = isoToCalendarDayKey(booking.end_date);
    if (!startKey || !endKey) {
      continue;
    }
    for (const day of eachCalendarDayInRange(startKey, endKey)) {
      reserved.add(day);
    }
  }
  return reserved;
}

export type CalendarGridCell = {
  dayKey: string;
  day: number;
  inMonth: boolean;
};

/** Six-week grid covering `viewYear` / `viewMonthIndex` (Sunday-first). */
export function buildMonthCalendarGrid(viewYear: number, viewMonthIndex: number): CalendarGridCell[] {
  const first = new Date(viewYear, viewMonthIndex, 1);
  const startPad = first.getDay();
  const gridStart = new Date(viewYear, viewMonthIndex, 1 - startPad);
  const cells: CalendarGridCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    cells.push({
      dayKey: toCalendarDayKey(d.getFullYear(), d.getMonth(), d.getDate()),
      day: d.getDate(),
      inMonth: d.getMonth() === viewMonthIndex,
    });
  }
  return cells;
}

export function normalizeCalendarRangeKeys(startKey: string, endKey: string): { start: string; end: string } {
  return startKey <= endKey ? { start: startKey, end: endKey } : { start: endKey, end: startKey };
}

export function isDayInPreviewRange(
  dayKey: string,
  anchorKey: string | null,
  hoverKey: string | null,
): boolean {
  if (!anchorKey || !hoverKey) {
    return false;
  }
  const { start, end } = normalizeCalendarRangeKeys(anchorKey, hoverKey);
  return dayKey >= start && dayKey <= end;
}

export function shiftMonth(viewYear: number, viewMonthIndex: number, delta: number): {
  year: number;
  monthIndex: number;
} {
  const d = new Date(viewYear, viewMonthIndex + delta, 1);
  return { year: d.getFullYear(), monthIndex: d.getMonth() };
}

/** Long local date and time for hourly booking summary. */
export function formatCalendarDayTimeLabel(
  dayKey: string,
  timeHm: string,
  language: 'en' | 'he',
): string {
  const p = parseCalendarDayKey(dayKey);
  if (!p || !/^\d{2}:\d{2}$/.test(timeHm)) {
    return formatCalendarDayKeyLabel(dayKey, language);
  }
  const [hh, mm] = timeHm.split(':').map((x) => parseInt(x, 10));
  const locale = language === 'he' ? 'he-IL' : 'en-US';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(new Date(p.year, p.monthIndex, p.day, hh, mm, 0, 0));
}

/** Long local date for calendar summary (e.g. June 2, 2026 / 2 ביוני 2026). */
export function formatCalendarDayKeyLabel(dayKey: string, language: 'en' | 'he'): string {
  const p = parseCalendarDayKey(dayKey);
  if (!p) {
    return dayKey;
  }
  const locale = language === 'he' ? 'he-IL' : undefined;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(
    new Date(p.year, p.monthIndex, p.day),
  );
}

export function calendarDayKeyToScheduleDisplay(
  dayKey: string,
): { y: string; m: string; d: string } | null {
  const p = parseCalendarDayKey(dayKey);
  if (!p) {
    return null;
  }
  return { y: String(p.year), m: String(p.monthIndex), d: String(p.day) };
}
